import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PrinterOutlined } from '@ant-design/icons';
import { findGiftCardCandidates } from '../../utils/giftCardCandidates';
import { createStandardPackingSlipMargins } from '../../utils/packingSlipHtml';
import {
  diagnoseQzProfiles,
  getQzPrintReadiness,
  loadQzPrintSettings,
  reconcileQzPrintSettings,
  saveQzPrintSettings,
} from '../../services/qzPrintService';
import { analyzePrintSeparatorGroups } from '../../utils/printJobSeparators';
import {
  coercePackingSlipFormat,
  PACKING_SLIP_FORMAT,
  profileKeysForPackingFormat,
} from '../../utils/packingSlipPrintDefaults';
import QzPrinterSettingsPanel from './QzPrinterSettingsPanel';
import './PackingPrePrintDialog.css';

const EMPTY_COPIES = Object.freeze({});

const orderKey = (order) =>
  String(order?.source_id || order?.sourceId || order?.orderId || order?.id || '').replace(
    /^#/,
    '',
  );

const orderLabel = (order) => {
  const company =
    order?.shipping?.company ||
    order?.customer?.company ||
    order?.companyName ||
    order?.customerCompany ||
    '';
  return company ? `${orderKey(order)} · ${company}` : orderKey(order);
};

const clampCopies = (value, fallback = 1) =>
  Math.max(1, Math.min(99, parseInt(value, 10) || fallback));

export default function PackingPrePrintDialog({
  open,
  orders = [],
  initialFormat = 'standard',
  initialSettings = {},
  initialCopiesByOrder = EMPTY_COPIES,
  defaultsNote = '',
  busy = false,
  onClose,
  onConfirm,
}) {
  const printNowRef = useRef(null);
  const didFocusPrintRef = useRef(false);
  const [format, setFormat] = useState(initialFormat);
  const [settings, setSettings] = useState({});
  const [copiesByOrder, setCopiesByOrder] = useState({});
  const [giftCandidates, setGiftCandidates] = useState([]);
  const [selectedGiftCards, setSelectedGiftCards] = useState({});
  const [giftMessages, setGiftMessages] = useState({});
  const [directPrint, setDirectPrint] = useState(false);
  const [includeSeparators, setIncludeSeparators] = useState(false);
  const [qzSettings, setQzSettings] = useState(() => loadQzPrintSettings());
  const [qzStatus, setQzStatus] = useState({
    checking: false,
    ready: false,
    message: 'QZ Tray has not been checked.',
  });

  useEffect(() => {
    if (!open) {
      didFocusPrintRef.current = false;
      return undefined;
    }
    const defaultCopies = clampCopies(initialSettings?.copiesPerOrder, 1);
    setFormat(coercePackingSlipFormat(initialFormat));
    setIncludeSeparators(false);
    setQzSettings(loadQzPrintSettings());
    setSettings({
      paperSize: initialSettings?.paperSize || 'A4',
      orientation: initialSettings?.orientation || 'portrait',
      showPrices: initialSettings?.showPrices !== false,
      showSignatureLines: initialSettings?.showSignatureLines !== false,
      includeDuplicate: initialSettings?.includeDuplicate === true,
      margins: createStandardPackingSlipMargins(),
    });
    setCopiesByOrder(
      orders.reduce((map, order) => {
        const key = orderKey(order);
        if (key) map[key] = clampCopies(initialCopiesByOrder?.[key], defaultCopies);
        return map;
      }, {}),
    );
    const candidates = findGiftCardCandidates(orders);
    setGiftCandidates(candidates);
    setSelectedGiftCards(
      candidates.reduce((map, candidate) => {
        map[candidate.id] = candidate.likely;
        return map;
      }, {}),
    );
    setGiftMessages(
      candidates.reduce((map, candidate) => {
        map[candidate.id] = candidate.message;
        return map;
      }, {}),
    );
    let active = true;
    setQzStatus({ checking: true, ready: false, message: 'Checking QZ Tray…' });
    getQzPrintReadiness({ force: true }).then((status) => {
      if (!active) return;
      setQzStatus({ ...status, checking: false });
      setDirectPrint(status.ready);
      if (status.ready) {
        setQzSettings((previous) => {
          const next = reconcileQzPrintSettings(previous, status.printerDetails);
          saveQzPrintSettings(next);
          return next;
        });
      }
    });
    return () => {
      active = false;
    };
  }, [open, orders, initialFormat, initialSettings, initialCopiesByOrder]);

  useEffect(() => {
    if (!open || busy || didFocusPrintRef.current) return undefined;
    const timer = window.setTimeout(() => {
      printNowRef.current?.focus();
      didFocusPrintRef.current = true;
    }, 60);
    return () => window.clearTimeout(timer);
  }, [open, busy]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== 'Escape' || busy) return;
      event.preventDefault();
      onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, busy, onClose]);

  const totalLabels = useMemo(
    () => orders.reduce((sum, order) => sum + clampCopies(copiesByOrder[orderKey(order)]), 0),
    [orders, copiesByOrder],
  );
  const selectedGiftCardCount = useMemo(
    () =>
      giftCandidates.filter(
        (candidate) =>
          selectedGiftCards[candidate.id] &&
          String(giftMessages[candidate.id] || '').trim(),
      ).length,
    [giftCandidates, selectedGiftCards, giftMessages],
  );
  const separatorAnalysis = useMemo(
    () => analyzePrintSeparatorGroups(orders),
    [orders],
  );
  const qzProfileKeys = useMemo(() => {
    const keys = [...profileKeysForPackingFormat(format)];
    if (includeSeparators) keys.push('separator');
    if (selectedGiftCardCount) keys.push('giftCard');
    return keys;
  }, [format, includeSeparators, selectedGiftCardCount]);
  const profileDiagnostics = useMemo(
    () =>
      qzStatus.ready
        ? diagnoseQzProfiles(qzSettings, qzStatus.printerDetails || [], qzProfileKeys)
        : [],
    [qzProfileKeys, qzSettings, qzStatus],
  );
  const qzWarnings = useMemo(
    () => [
      ...(qzStatus.warnings || []),
      ...profileDiagnostics.flatMap((item) => item.warnings || []),
    ],
    [profileDiagnostics, qzStatus.warnings],
  );

  if (!open) return null;

  const updateMargin = (side, value) => {
    setSettings((previous) => ({
      ...previous,
      margins: {
        ...createStandardPackingSlipMargins(),
        ...(previous.margins || {}),
        [side]: Math.max(0, Math.min(60, Number(value) || 0)),
      },
    }));
  };
  const applyStandardMargins = () => {
    setSettings((previous) => ({
      ...previous,
      margins: createStandardPackingSlipMargins(),
    }));
  };
  const actionsLocked = busy || !orders.length;
  const footerReason = !orders.length
    ? 'Load or select orders before printing.'
    : busy
      ? directPrint
        ? 'Building pages and sending them to the printer…'
        : 'Preparing this job…'
      : '';
  const submit = (queueOnly) =>
    onConfirm?.({
      format,
      settings,
      copiesByOrder,
      directPrint: queueOnly ? false : directPrint,
      queueOnly,
      includeSeparators: (queueOnly || directPrint) && includeSeparators,
      qzSettings,
      giftCards: giftCandidates
        .filter(
          (candidate) =>
            selectedGiftCards[candidate.id] &&
            String(giftMessages[candidate.id] || '').trim(),
        )
        .map((candidate) => ({
          order: candidate.order,
          orderKey: candidate.orderKey,
          message: String(giftMessages[candidate.id] || '').trim(),
          sourceLabel: candidate.sourceLabel,
        })),
    });

  return (
    <div className="ppp-overlay" role="presentation">
      <section
        className="ppp-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ppp-title"
      >
        <header className="ppp-header">
          <div>
            <p className="ppp-eyebrow">London Smoke &amp; Cure</p>
            <h2 id="ppp-title">Prepare print job</h2>
            <p>
              {orders.length} order{orders.length === 1 ? '' : 's'} · {totalLabels}{' '}
              label{totalLabels === 1 ? '' : 's'}
              {giftCandidates.length
                ? ` · ${selectedGiftCardCount} A6 card${selectedGiftCardCount === 1 ? '' : 's'}`
                : ''}
            </p>
          </div>
          <button type="button" className="ppp-close" onClick={onClose} disabled={busy} aria-label="Close">×</button>
        </header>

        <div className="ppp-body">
          <fieldset className="ppp-section">
            <legend>Print format</legend>
            {defaultsNote ? <p className="ppp-defaults-note">{defaultsNote}</p> : null}
            <div className="ppp-format-grid">
              <label className={format === PACKING_SLIP_FORMAT.STANDARD ? 'is-selected' : ''}>
                <input
                  type="radio"
                  name="packing-format"
                  value="standard"
                  checked={format === PACKING_SLIP_FORMAT.STANDARD}
                  onChange={() => setFormat(PACKING_SLIP_FORMAT.STANDARD)}
                />
                <span><b>Standard</b><small>Normal packing slip</small></span>
              </label>
              <label className={format === PACKING_SLIP_FORMAT.CHEETAH ? 'is-selected' : ''}>
                <input
                  type="radio"
                  name="packing-format"
                  value="cheetah"
                  checked={format === PACKING_SLIP_FORMAT.CHEETAH}
                  onChange={() => setFormat(PACKING_SLIP_FORMAT.CHEETAH)}
                />
                <span><b>Cheetah LZ17</b><small>Sticky label and in-box stub</small></span>
              </label>
              <label className={format === PACKING_SLIP_FORMAT.GO4 ? 'is-selected' : ''}>
                <input
                  type="radio"
                  name="packing-format"
                  value="go4"
                  checked={format === PACKING_SLIP_FORMAT.GO4}
                  onChange={() => setFormat(PACKING_SLIP_FORMAT.GO4)}
                />
                <span>
                  <b>GO4</b>
                  <small>A4 slip + GoDEX outside 3.94×5.91</small>
                </span>
              </label>
            </div>
          </fieldset>

          <fieldset className="ppp-section">
            <legend>Copies</legend>
            <div className="ppp-orders">
              {orders.map((order) => {
                const key = orderKey(order);
                return (
                  <label key={key} className="ppp-order-row">
                    <span>{orderLabel(order)}</span>
                    <span className="ppp-order-qty">
                      Labels
                      <input
                        type="number"
                        min="1"
                        max="99"
                        value={copiesByOrder[key] || 1}
                        onChange={(event) =>
                          setCopiesByOrder((previous) => ({
                            ...previous,
                            [key]: clampCopies(event.target.value),
                          }))
                        }
                        onBlur={(event) =>
                          setCopiesByOrder((previous) => ({
                            ...previous,
                            [key]: clampCopies(event.target.value),
                          }))
                        }
                      />
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>

          <details className="ppp-disclosure">
            <summary>Printer &amp; paper</summary>
            <div className="ppp-disclosure__body">
              <label className={`ppp-direct-print${qzStatus.ready ? ' is-ready' : ''}`}>
                <input
                  type="checkbox"
                  checked={directPrint}
                  disabled={!qzStatus.ready || qzStatus.checking}
                  onChange={(event) => {
                    setDirectPrint(event.target.checked);
                    if (!event.target.checked) setIncludeSeparators(false);
                  }}
                />
                <span>
                  <b>Print directly with QZ Tray</b>
                  <small>{qzStatus.message}</small>
                  {qzStatus.connected && !qzStatus.signed && (
                    <small>Unsigned local mode. Choose Allow and Remember when QZ asks.</small>
                  )}
                  <small>
                    {format === PACKING_SLIP_FORMAT.CHEETAH
                      ? 'Cheetah print profile'
                      : format === PACKING_SLIP_FORMAT.GO4
                        ? 'Standard A4 + GO4 GoDEX outside profiles'
                        : 'Standard print profile'}
                    {selectedGiftCardCount ? ' · A6 card profile' : ''}
                  </small>
                </span>
              </label>
              <button
                type="button"
                className="toolkit-btn toolkit-btn--ghost"
                disabled={qzStatus.checking}
                onClick={async () => {
                  setQzStatus((previous) => ({ ...previous, checking: true }));
                  const status = await getQzPrintReadiness({ force: true });
                  setQzStatus({ ...status, checking: false });
                }}
              >
                {qzStatus.checking ? 'Rechecking…' : 'Recheck QZ and printers'}
              </button>
              {qzStatus.ready && separatorAnalysis.hasMultiple && (
                <label className="ppp-separator-option">
                  <input
                    type="checkbox"
                    checked={includeSeparators}
                    disabled={!directPrint}
                    onChange={(event) => setIncludeSeparators(event.target.checked)}
                  />
                  <span>
                    <b>Insert A4 Start-of segment sheets</b>
                    <small>
                      After each shipping / dispatch / delivery set of labels, Tray 1 prints a
                      Start of cover with that segment pick list, methods, and order count.
                    </small>
                  </span>
                </label>
              )}
              {directPrint && (
                <>
                  <div className="ppp-routing-summary">
                    {profileDiagnostics.map((diagnostic) => {
                      const value = qzSettings[diagnostic.profileKey] || {};
                      return (
                        <p key={diagnostic.profileKey}>
                          <b>{diagnostic.profileKey}</b>: {value.printer} ·{' '}
                          {value.printerTray || 'driver default'} · {value.paperSizeName}
                        </p>
                      );
                    })}
                  </div>
                  <QzPrinterSettingsPanel
                    profileKeys={qzProfileKeys}
                    printerDetails={qzStatus.printerDetails || []}
                    settings={qzSettings}
                    onChange={(next) => {
                      setQzSettings(next);
                      saveQzPrintSettings(next);
                    }}
                  />
                </>
              )}
              <div className="ppp-checks">
                <label>
                  <input
                    type="checkbox"
                    checked={settings.showPrices === true}
                    onChange={(event) =>
                      setSettings((previous) => ({ ...previous, showPrices: event.target.checked }))
                    }
                  />
                  Show prices
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={settings.showSignatureLines === true}
                    onChange={(event) =>
                      setSettings((previous) => ({
                        ...previous,
                        showSignatureLines: event.target.checked,
                      }))
                    }
                  />
                  Signature lines
                </label>
                <label
                  className={
                    format === PACKING_SLIP_FORMAT.CHEETAH ? 'is-disabled' : ''
                  }
                >
                  <input
                    type="checkbox"
                    disabled={format === PACKING_SLIP_FORMAT.CHEETAH}
                    checked={settings.includeDuplicate === true}
                    onChange={(event) =>
                      setSettings((previous) => ({
                        ...previous,
                        includeDuplicate: event.target.checked,
                      }))
                    }
                  />
                  Duplicate summary
                </label>
              </div>
              <fieldset
                className="ppp-paper-block"
                disabled={format === PACKING_SLIP_FORMAT.CHEETAH}
              >
                {format === PACKING_SLIP_FORMAT.CHEETAH && (
                  <p className="ppp-locked">Cheetah geometry is locked to LZ17 stock.</p>
                )}
                {format === PACKING_SLIP_FORMAT.GO4 && (
                  <p className="ppp-locked">
                    GO4 prints Standard A4 on Tray 1 plus a GoDEX outside label
                    (3.94×5.91). Paper settings below apply to the A4 slip only.
                  </p>
                )}
                <div className="ppp-paper-grid">
                  <label>
                    Paper
                    <select
                      value={settings.paperSize || 'A4'}
                      onChange={(event) =>
                        setSettings((previous) => ({ ...previous, paperSize: event.target.value }))
                      }
                    >
                      <option value="A4">A4</option>
                      <option value="A5">A5</option>
                      <option value="Letter">Letter</option>
                    </select>
                  </label>
                  <label>
                    Orientation
                    <select
                      value={settings.orientation || 'portrait'}
                      onChange={(event) =>
                        setSettings((previous) => ({
                          ...previous,
                          orientation: event.target.value,
                        }))
                      }
                    >
                      <option value="portrait">Portrait</option>
                      <option value="landscape">Landscape</option>
                    </select>
                  </label>
                </div>
                <div className="ppp-margin-row">
                  <div className="ppp-margin-grid">
                    {['top', 'right', 'bottom', 'left'].map((side) => (
                      <label key={side}>
                        {side}
                        <input
                          type="number"
                          min="2"
                          max="60"
                          value={settings.margins?.[side] ?? 15}
                          onChange={(event) => updateMargin(side, event.target.value)}
                          onBlur={(event) => updateMargin(side, event.target.value)}
                        />
                      </label>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="toolkit-btn toolkit-btn--ghost toolkit-btn--compact"
                    onClick={applyStandardMargins}
                  >
                    15 mm all round
                  </button>
                </div>
              </fieldset>
            </div>
          </details>

          {giftCandidates.length > 0 && (
            <details className="ppp-disclosure">
              <summary>
                Gift cards · {selectedGiftCardCount} selected
              </summary>
              <div className="ppp-disclosure__body">
                <p className="ppp-gift-help">
                  Possible customer messages were found. Strong matches are preselected;
                  delivery-style notes are shown for review but left unchecked.
                </p>
                <div className="ppp-gift-candidates">
                  {giftCandidates.map((candidate) => (
                    <div
                      key={candidate.id}
                      className={`ppp-gift-candidate${
                        selectedGiftCards[candidate.id] ? ' is-selected' : ''
                      }`}
                    >
                      <label className="ppp-gift-heading">
                        <input
                          type="checkbox"
                          checked={Boolean(selectedGiftCards[candidate.id])}
                          onChange={(event) =>
                            setSelectedGiftCards((previous) => ({
                              ...previous,
                              [candidate.id]: event.target.checked,
                            }))
                          }
                        />
                        <span>
                          <b>Order #{candidate.orderKey}</b>
                          <small>
                            {candidate.sourceLabel} ·{' '}
                            {candidate.likely ? 'Likely gift message' : 'Please review'}
                          </small>
                        </span>
                      </label>
                      <textarea
                        rows="4"
                        value={giftMessages[candidate.id] || ''}
                        onChange={(event) =>
                          setGiftMessages((previous) => ({
                            ...previous,
                            [candidate.id]: event.target.value,
                          }))
                        }
                        aria-label={`A6 card message for order ${candidate.orderKey}`}
                      />
                    </div>
                  ))}
                </div>
                <p className="ppp-gift-help">
                  Selected messages print as landscape A6 cards in one separate dialog
                  after the label job.
                </p>
              </div>
            </details>
          )}

          <p className="ppp-browser-note">
            {directPrint
              ? 'QZ sends the job directly to the configured Brother tray. No browser print dialog is opened.'
              : 'Confirming opens one browser print dialog for this complete job. Requested copies are already included, so leave the browser Copies field at 1.'}
          </p>
        </div>

        <footer className="ppp-footer">
          {directPrint && qzWarnings.length > 0 ? (
            <div className="ppp-footer-warnings" role="status">
              <b>Printer notes — Print now stays available</b>
              <ul>
                {qzWarnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {footerReason ? (
            <p className="ppp-footer-reason" role="status">
              {footerReason}
            </p>
          ) : null}
          <div className="ppp-footer__actions">
            <button type="button" className="toolkit-btn toolkit-btn--ghost" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button
              type="button"
              className="toolkit-btn toolkit-btn--secondary"
              disabled={actionsLocked}
              onClick={() => submit(true)}
            >
              Add to print queue
            </button>
            <button
              ref={printNowRef}
              type="button"
              className="toolkit-btn toolkit-btn--primary"
              disabled={actionsLocked}
              onClick={() => submit(false)}
            >
              <PrinterOutlined />{' '}
              {busy
                ? directPrint
                  ? 'Sending to printer…'
                  : 'Preparing…'
                : directPrint
                  ? 'Print now'
                  : 'Continue to print'}
            </button>
          </div>
        </footer>
      </section>
    </div>
  );
}
